/**
 * A run's configuration, as one document.
 *
 * The installer declares 108 variables and the run form has eleven boxes. What
 * closed that gap until now was `curl`: the index signature on
 * `TerraformVariables`, `additionalVariables`, the API's pass-through and the
 * runner wrapper's `config.json` were all already in place — there was simply
 * nowhere to type.
 *
 * **The document is the state, and the form is a projection over it.** Not two
 * states kept in step. That arrangement has one characteristic failure — a
 * variable the form cannot express is silently dropped when someone switches
 * back to the form and submits — and the failure is not hypothetical: it exists
 * today in miniature, because `formValuesFrom` read eleven keys out of a
 * remembered variables document and ignored the rest, so an apply after a plan
 * with extra variables quietly lost them.
 *
 * This module holds the parts of that model with no React in them, so they can
 * be tested as functions: what the document starts as, how a value typed into a
 * row becomes a `JsonValue`, and how one is rendered back.
 *
 * ## What it edits
 *
 * `variables`, not `config.json`. The other half of that file, `account_config`,
 * is assembled **inside the container** — region, bucket and state key, with no
 * credentials in it, those coming from the role the runner assumes there. None
 * of it is a person's to set, and the bucket and key in particular are how an
 * instance finds its own Terraform state: a typo in either builds a second
 * instance while the console still shows one.
 */

import type { JsonValue } from '@qim/shared'

/** Whether a `JsonValue` is a plain object, which is what a block must be. */
export const isJsonObject = (value: JsonValue | undefined): value is { [k: string]: JsonValue } =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * The top-level keys the form has controls for.
 *
 * Everything else gets a name/value row — **not** folded behind an "advanced"
 * link. A variable somebody set is a variable that changes the deployment, and
 * hiding it is exactly how a run goes out different from the one that was
 * reviewed. IM-62 is what turns those generic rows into typed controls carrying
 * the image's own descriptions and defaults.
 */
export const CURATED_VARIABLES: readonly string[] = [
  'deployment_id',
  'qrvey_chart_version',
  'initial_admin_email',
  'registry_user',
  'registry_key',
  'customer_info',
  'staging_instance',
  'opensearch_config',
  'es_config',
]

const CURATED = new Set(CURATED_VARIABLES)

/** The keys of a document the form has no control for, in document order. */
export const extraVariableNames = (
  document: Readonly<Record<string, JsonValue>>,
): readonly string[] => Object.keys(document).filter((name) => !CURATED.has(name))

/** The search backend a document is asking for, which is which block it carries. */
export type SearchBackendChoice = 'opensearch' | 'es'

export const OPENSEARCH_DEFAULT: { [k: string]: JsonValue } = {
  enabled: true,
  instance_type: 'r6g.large.search',
  instance_count: 2,
  volume_size: 100,
}

export const ES_DEFAULT: { [k: string]: JsonValue } = {
  size: 'small',
  count: 1,
  storage: '50Gi',
}

export const searchBackendOf = (
  document: Readonly<Record<string, JsonValue>>,
): SearchBackendChoice => (document['es_config'] !== undefined ? 'es' : 'opensearch')

/**
 * What a new run's document starts as.
 *
 * The search block's sizes are prefilled, unlike the deployment id. The rule
 * that nothing is prefilled silently is about values that decide *which
 * instance* a run touches; an instance class is a form default, and making
 * someone retype a known-good one invites a typo instead of preventing an
 * accident.
 *
 * `customer_info` is present from the start because the installer requires all
 * four of its fields and has no default for any — its absence is why the first
 * run ever started from the console failed (IM-89). Company defaults to the
 * customer id: a label on the deployment, not a value that decides which
 * instance a run touches.
 *
 * The numbers are numbers here rather than strings that get converted at send
 * time, which is the whole point of holding a document: `volume_size` is 100 and
 * stays 100 through every round trip between the two views.
 */
export const initialDocument = (
  deploymentId: string | undefined,
  tenantId: string,
): Record<string, JsonValue> => ({
  deployment_id: deploymentId ?? '',
  customer_info: { firstname: '', lastname: '', email: '', company: tenantId },
  /*
   * Switches the chart and images to `qrveydev.azurecr.io`.
   *
   * Off by default, because it is what a *customer* deployment wants. Qrvey's
   * own test accounts need it on, and their registry tokens are scoped to the
   * dev registry — so a run against one with this off fails at image pull,
   * having got all the way through the plan first.
   */
  staging_instance: false,
  opensearch_config: { ...OPENSEARCH_DEFAULT },
})

/**
 * What someone typed into a value row, as a `JsonValue`.
 *
 * JSON first, and the raw text as a string when it does not parse. That order
 * is what keeps `100` a number and `true` a boolean — a form that turns `100`
 * into `"100"` produces a plan failure nobody can map back to what they typed —
 * while still letting an ordinary string be typed without quotes.
 *
 * The inference is never silent: the row renders the resolved type beside the
 * box, and `displayValue` re-renders the committed value canonically on blur,
 * so a string that was typed bare comes back quoted. Someone who wants the
 * *string* `"100"` can see that they have a number and add the quotes.
 */
export const parseValue = (text: string): JsonValue => {
  try {
    return JSON.parse(text) as JsonValue
  } catch {
    return text
  }
}

/**
 * A committed value as text to edit.
 *
 * Always `JSON.stringify`, including for strings, so the round trip is exact.
 * Rendering a string bare would read better and would lose types: the string
 * `"true"` would come back as a boolean the next time the row was touched.
 */
export const displayValue = (value: JsonValue): string => JSON.stringify(value) ?? 'null'

/** What to call a value's type beside its row. */
export const typeLabel = (value: JsonValue): string => {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'list'
  if (typeof value === 'object') return 'object'
  return typeof value
}

export interface DocumentText {
  readonly document?: Record<string, JsonValue>
  readonly problem?: string
}

/**
 * A document typed as JSON, or why it is not one.
 *
 * Refused here rather than sent and refused by the API: the position of the
 * fault is in the parser's message and nowhere else, and "unexpected token at
 * position 412" arrives from the API as a 400 with no position at all.
 *
 * An array or a scalar parses perfectly well and is still not a variables
 * document, so that is its own message — `JSON.parse('[1,2]')` succeeding is
 * exactly the case a bare try/catch would wave through.
 */
export const readDocumentText = (text: string): DocumentText => {
  if (text.trim() === '') return { document: {} }
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch (error: unknown) {
    return { problem: error instanceof Error ? error.message : 'The document is not valid JSON.' }
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return {
      problem: `The document must be a JSON object of variable names and values, not ${
        Array.isArray(parsed) ? 'a list' : typeof parsed
      }.`,
    }
  }
  return { document: parsed as Record<string, JsonValue> }
}

/** The document as text for the editor, two-space indented like the fixtures. */
export const writeDocumentText = (document: Readonly<Record<string, JsonValue>>): string =>
  JSON.stringify(document, null, 2)
